import React, { forwardRef } from 'react'

import { ResourcePermissionGate } from '@/components/common/PermissionGate/ResourcePermissionGate'
import type { PursuitPermissionGateProps } from '@/components/common/PermissionGate/types'
import {
  cloneElementWithDisabled,
  cloneElementWithReadOnly,
} from '@/components/common/PermissionGate/utils'

import { useActivePursuitId } from '@/hooks/pursuits/useActivePursuitId'

import type { UUID } from '@/types/Id'

/**
 * Higher-Order Component for pursuit-level permission checks.
 * Automatically uses the active pursuit ID and wraps ResourcePermissionGate.
 *
 * @example
 * // Hide create button for users without write permission
 * <PursuitPermissionGate permission="write" fallback="hide">
 *   <Button>Create Document</Button>
 * </PursuitPermissionGate>
 *
 * @example
 * // Disable with tooltip
 * <PursuitPermissionGate
 *   permission="write"
 *   fallback="disable"
 *   tooltip="You need write access to create pursuits"
 *   asChild
 * >
 *   <DropdownMenuItem>Create</DropdownMenuItem>
 * </PursuitPermissionGate>
 */
export const PursuitPermissionGate = forwardRef<
  HTMLElement,
  PursuitPermissionGateProps
>((props, ref) => {
  const { activePursuitId } = useActivePursuitId()
  const { fallback = 'hide', loadingComponent = null, children } = props

  // If no active pursuit, apply fallback behavior (can't check permissions without ID)
  // This prevents accidental exposure of gated content during loading or errors
  if (!activePursuitId) {
    switch (fallback) {
      case 'hide':
        return loadingComponent
      case 'disable':
        return React.isValidElement(children) ? (
          cloneElementWithDisabled(children)
        ) : (
          <>{children}</>
        )
      case 'readonly':
        return React.isValidElement(children) ? (
          cloneElementWithReadOnly(children)
        ) : (
          <>{children}</>
        )
      default:
        return loadingComponent
    }
  }

  // Simply wrap ResourcePermissionGate with pursuit context
  return (
    <ResourcePermissionGate
      {...props}
      ref={ref}
      resourceType="pursuit"
      resourceId={activePursuitId as UUID}
    />
  )
})

PursuitPermissionGate.displayName = 'PursuitPermissionGate'
